import { Plus, Trash2, Truck, Phone, Sparkles, Users } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import AddSupplierModal from "./AddSupplierModal"
import { useDispatch, useSelector } from "react-redux"
import { suppliersData } from "../../store/actions"
import { deleteSupplier } from "../../api/suppliers/post"

export default function Suppliers() {
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const suppliersdata = useSelector((state: any) => state.UserReducer.suppliersdata || [])
  const { store } = userdata

  const dispatch = useDispatch()

  const [suppliers, setSuppliers] = useState<any[]>(suppliersdata || [])
  const [open, setOpen] = useState(false)
  const [updateUI, setUpdateUI] = useState(Math.floor(Math.random() * 100))

  useEffect(() => {
    if (!store?._id) return
    loadSuppliers()
  }, [updateUI, store?._id])

  useEffect(() => {
    setSuppliers(Array.isArray(suppliersdata) ? suppliersdata : [])
  }, [suppliersdata])

  const loadSuppliers = async () => {
    dispatch(suppliersData(store?._id))
  }

  const handleDelete = async (id: string) => {
    await deleteSupplier(id, store?._id)
    setSuppliers((prev) => prev.filter((s) => s._id !== id))
  }

  const suppliersWithPhone = useMemo(
    () => suppliers.filter((s) => String(s?.phone || "").trim().length > 0).length,
    [suppliers]
  )

  return (
    <>
      <div className="space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-700 to-blue-900 p-6 sm:p-7 text-white shadow-xl">
          <div className="pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full bg-cyan-300/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> Partner Network
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold">Fournisseurs</h1>
              <p className="mt-2 text-sm text-white/85 max-w-2xl">Gérez vos partenaires d'approvisionnement et gardez leurs contacts accessibles.</p>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-blue-700 hover:bg-blue-50 transition font-semibold"
            >
              <Plus size={18} />
              Ajouter fournisseur
            </button>
          </div>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard
            label="Fournisseurs actifs"
            value={suppliers.length}
            icon={<Users size={18} />}
            color="from-blue-600 to-cyan-600"
            ring="ring-blue-100"
          />
          <StatCard
            label="Contacts téléphoniques"
            value={suppliersWithPhone}
            icon={<Phone size={18} />}
            color="from-emerald-600 to-green-500"
            ring="ring-emerald-100"
          />
        </div>

        <div className="hidden md:block bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">Répertoire fournisseurs</p>
            <p className="text-xs text-slate-500 mt-0.5">Visualisez rapidement le nom et le contact de chaque fournisseur.</p>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-6 py-3 text-left">Nom</th>
                <th className="px-6 py-3 text-left">Téléphone</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {suppliers.map((s) => (
                <tr key={s._id} className="border-t border-slate-100 hover:bg-slate-50/70 transition">
                  <td className="px-6 py-4">
                    <div className="inline-flex items-center gap-2 text-slate-800 font-medium">
                      <span className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Truck size={15} />
                      </span>
                      {s.name}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-slate-600">{s.phone || "-"}</td>

                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDelete(s._id)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-rose-700 hover:bg-rose-100 transition"
                    >
                      <Trash2 size={15} /> Supprimer
                    </button>
                  </td>
                </tr>
              ))}

              {suppliers.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-slate-500">
                    Aucun fournisseur enregistré
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {suppliers.map((s) => (
            <div key={s._id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 flex items-start justify-between">
                <div className="inline-flex items-center gap-2 text-slate-800 font-semibold">
                  <span className="h-8 w-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                    <Truck size={15} />
                  </span>
                  {s.name}
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Téléphone</span>
                  <span className="font-medium text-slate-700">{s.phone || "-"}</span>
                </div>

                <button
                  onClick={() => handleDelete(s._id)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 py-2 text-rose-700 hover:bg-rose-100 transition"
                >
                  <Trash2 size={15} /> Supprimer
                </button>
              </div>
            </div>
          ))}

          {suppliers.length === 0 && (
            <div className="text-center text-slate-500 py-10">Aucun fournisseur enregistré</div>
          )}
        </div>
      </div>

      <AddSupplierModal
        currentStore={store}
        isOpen={open}
        onClose={() => setOpen(false)}
        onAdded={(s: any) => {
          dispatch(suppliersData(store?._id))
          setUpdateUI(Math.floor(Math.random() * 100))
          setSuppliers((prev) => [s, ...prev])
        }}
      />
    </>
  )
}

function StatCard({
  label,
  value,
  icon,
  color,
  ring,
}: {
  label: string
  value: string | number
  icon: React.ReactNode
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center gap-4">
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  )
}
